import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { canManagePayments } from "../accessControl";
import { callApi, formatDate, formatMoney } from "../Helpers";
import { getInlineLoader } from "../Helpers";
import { MdArrowOutward, MdOutlinePayments, MdQrCode2, MdVerified, MdEdit, MdPlusOne, MdAdd } from "react-icons/md";
import PaymentReceipt from "./PaymentReceipt";

export default function View({ uuid }) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [data, setData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  async function init() {
    setReady(false);
    await Promise.all([getData()]);
    setReady(true);
  }

  async function getData() {
    await callApi("get-payment-data", { uuid }).then((response) => {
      if (response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError("Could not fetch data");
      }
    });
  }

  useEffect(() => {
    init();
  }, [uuid]);

  if (ready && data) {
    const showPaymentAction = canManagePayments(appContext.currentUserData);

    if (showReceipt) {
      return (
        <div className="container mt-4">
          <button className="btn btn-secondary mb-3" onClick={() => setShowReceipt(false)}>
            &larr; Back to Details
          </button>
          <div className="d-flex justify-content-center">
            <PaymentReceipt paymentData={data} />
          </div>
        </div>
      );
    }

    return (
      <div className="container py-3">
        <div className="detail-shell">
          
          {/* OPTIMIZED RESPONSIVE HEADER SECTION */}
          <div className="detail-header d-flex flex-column flex-md-row align-items-start justify-content-between gap-3 pb-3 border-bottom">
            <div>
              <h4 className="main-section-title mb-2 font-bold">{data.fullname || "Payment record"}</h4>
              <div className="d-flex align-items-center gap-2 flex-wrap text-muted small">
                <span className="font-bold text-muted p-2 rounded" style={{background:'rgba(0,0,0,.15)'}}>{data.model}</span>
                <span className="font-bold text-muted p-2 rounded" style={{background:'rgba(0,0,0,.15)'}}>{data.phone}</span>
              </div>
            </div>

          </div>

          {/* SUMMARY GRID LAYOUT */}
          <div className="app-form-summary-grid mt-4">
            <div className="app-form-summary-card">
              <span>Amount Paid</span>
              <strong>{formatMoney(data.amount)}</strong>
            </div>
            <div className="app-form-summary-card">
              <span>Debt Total</span>
              <strong>{formatMoney(data.totalAmount)}</strong>
            </div>
            <div className="app-form-summary-card">
              <span>Remaining Balance</span>
              <strong>{formatMoney(data.balance)}</strong>
            </div>
          </div>

          {/* DYNAMIC QUICK ACTIONS PANEL CONTAINER */}
          <div className="detail-actions-card mt-4">
            <div className="mb-3">
              <h6 className="card-title mb-1 font-bold">Actions</h6>
            </div>

            {/* HIGHLY RESPONSIVE ACTION BUTTONS WRAPPER STACK */}
            <div className="d-flex flex-wrap align-items-center gap-2 app-header-actions-wrapper">
              {showPaymentAction && (
                <>
                  <button
                    className="btn btn-sm btn-dark btn-rounded app-cta-btn flex-fill flex-md-grow-0"
                    onClick={() => {
                      appContext.navTo({
                        item: "view",
                        subItem: "create-payment",
                        extraItem: data.debtID,
                      });
                    }}
                  >
                    <MdAdd size={18} /> Add payment
                  </button>

                  <button
                    className="btn btn-sm btn-primary btn-rounded app-cta-btn flex-fill flex-md-grow-0"
                    onClick={() => {
                      appContext.navTo({
                        item: "side-view",
                        subItem: "edit-payment",
                        extraItem: uuid, // Passes current payment tracking payload record explicitly
                      });
                    }}
                  >
                    <MdEdit size={18} /> Edit Payment
                  </button>
                </>
              )}

              <button
                className="btn btn-sm btn-outline-primary btn-rounded app-cta-btn flex-fill flex-md-grow-0"
                onClick={() => {
                  appContext.navTo({
                    item: "side-view",
                    subItem: "debt-options",
                    extraItem: data.debtID,
                  });
                }}
              >
                <MdArrowOutward size={18} /> View debt
              </button>

              <button
                className="btn btn-sm btn-outline-secondary btn-rounded app-cta-btn flex-fill flex-md-grow-0"
                onClick={() => setShowReceipt(true)}
              >
                <MdQrCode2 size={18} /> View Receipt
              </button>
            </div>
          </div>

          {/* HIDDEN INACTIVE DETAILS SECTION HOLDER */}
          <div className="detail-actions-card mt-4 d-none">
            <h6 className="card-title mb-4">Payment Details</h6>
            <div className="row">
              <div className="col-md-6 mb-4">
                <label className="form-label">Customer</label>
                <h6>{data.fullname || "Not set"}</h6>
              </div>
              <div className="col-md-6 mb-4">
                <label className="form-label">Phone</label>
                <h6>{data.phone || "Not set"}</h6>
              </div>
              <div className="col-md-6 mb-4">
                <label className="form-label">Motorcycle</label>
                <h6>{data.model || "Not set"}</h6>
              </div>
              <div className="col-md-6 mb-4">
                <label className="form-label">Debt Reference</label>
                <h6>{data.debtID || data.debtUuid || "Not set"}</h6>
              </div>
            </div>
          </div>

          {/* AUTHORIZATION AND AUDIT METRICS BANNER CONTAINER */}
          <div className="detail-actions-card mt-4 bg-light rounded border p-3">
            <div className="row g-3">
              <div className="col-sm-6">
                <label className="form-label small text-muted text-uppercase fw-semibold mb-1">Authorized By</label>
                <h6 className="d-flex align-items-center gap-2 mb-0 text-primary">
                  <MdVerified size={18} />
                  <span className="fw-bold" style={{ letterSpacing: "0.5px" }}>
                    {data.issuedByName || "System Authorized"}
                  </span>
                </h6>
              </div>
              <div className="col-sm-6">
                <label className="form-label small text-muted text-uppercase fw-semibold mb-1">ISSUED ON</label>
                <h6 className="mb-0 text-dark fw-semibold">{formatDate(data.dateCreated) || "Not set"}</h6>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }

  return <div className="container mSupportLoading">{getInlineLoader()}</div>;
}